import { useEffect, useState, type ReactNode } from 'react'
import { BadgeDollarSign, CircleAlert, Layers3, ReceiptText, TrendingUp, UserPlus, Users, WalletCards } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { api, brl, formatDate, integer } from '../api'
import { MetricCard } from '../components/MetricCard'
import { Empty, Loading } from '../components/Loading'
import { Pagination, usePagedItems } from '../components/Pagination'
import type { MonthlyPoint, Overview, Plan } from '../types'

export function DashboardPage() {
  const [data, setData] = useState<Overview | null>(null)
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    Promise.all([
      api<Overview>('/overview'),
      api<{ plans: Plan[] }>('/plans'),
    ]).then(([overview, planData]) => {
      setData(overview)
      setPlans(planData.plans)
      setError('')
    }).catch(reason => setError(reason.message))
  }, [])
  const customerPages = usePagedItems(data?.recent_customers ?? [])
  const orderPages = usePagedItems(data?.recent_orders ?? [])
  if (error) return <div className="notice error"><CircleAlert/>{error}</div>
  if (!data || !plans) return <Loading label="Preparando indicadores do negócio…"/>
  const planLabel = (planId: string) => plans.find(plan => plan.id === planId)?.name || planId || 'Sem plano'
  const cards = [
    { label: 'Clientes cadastrados', value: integer.format(data.totals.customers), detail: `${data.totals.expired} acessos encerrados`, icon: Users },
    { label: 'Planos cadastrados', value: integer.format(plans.length), detail: 'no catálogo de assinaturas', icon: Layers3 },
    { label: 'Receita líquida', value: brl.format(data.totals.net_revenue), detail: 'após taxas registradas', icon: BadgeDollarSign, featured: true },
    { label: 'Carteiras', value: integer.format(data.totals.wallets), detail: 'em todas as contas', icon: WalletCards },
    { label: 'Operações', value: integer.format(data.totals.operations), detail: 'registros no sistema', icon: ReceiptText },
  ]
  return <><PageHead eyebrow="VISÃO EXECUTIVA" title="Painel administrativo" text="Acompanhe os principais indicadores do AnotaCripto." action={<p className="data-update">Indicadores atualizados em {formatDate(data.generated_at)}.</p>}/>
    <section className="metric-grid admin-metrics" aria-label="Indicadores do negócio">{cards.map(card => <MetricCard key={card.label} {...card}/>)}</section>
    <section className="dashboard-grid"><article className="panel chart-panel"><PanelTitle icon={UserPlus} title="Crescimento de clientes" text="Novos cadastros nos últimos meses"/><Bars data={data.customer_growth}/></article><article className="panel chart-panel"><PanelTitle icon={TrendingUp} title="Receita líquida" text="Histórico mensal, após taxas"/><Bars data={data.revenue_history} currency/></article></section>
    <section className="dashboard-grid lower">
      <article className="panel">
        <PanelTitle icon={Users} title="Clientes recentes" text="Últimos perfis criados" count={data.recent_customers.length}/>
        {data.recent_customers.length ? <div className="compact-list">{customerPages.pageItems.map(customer => <div key={customer.id}><span className="list-avatar">{(customer.name || customer.email || 'C')[0].toUpperCase()}</span><div><strong>{customer.name || 'Cliente sem nome'}</strong><small>{customer.email || customer.id}</small></div><span className="badge">{planLabel(customer.plan_id)}</span></div>)}</div> : <Empty>Nenhum cliente cadastrado.</Empty>}
        <Pagination page={customerPages.page} totalPages={customerPages.totalPages} onChange={customerPages.setPage}/>
      </article>
      <article className="panel">
        <PanelTitle icon={ReceiptText} title="Pagamentos recentes" text="Aprovados e estornados" count={data.recent_orders.length}/>
        {data.recent_orders.length ? <div className="compact-list orders">{orderPages.pageItems.map(order => <div key={order.id}><span className={`order-status ${order.status}`}/><div><strong>{order.customer_name || order.customer_email || 'Cliente'}</strong><small>{formatDate(order.created_at)}</small></div><div className="list-value"><strong>{brl.format(order.amount)}</strong><small>{statusLabel(order.status)}</small></div></div>)}</div> : <Empty>Nenhum pagamento registrado.</Empty>}
        <Pagination page={orderPages.page} totalPages={orderPages.totalPages} onChange={orderPages.setPage}/>
      </article>
    </section>
  </>
}

export function PageHead({ eyebrow, title, text, action }: { eyebrow: string; title: string; text: string; action?: ReactNode }) {
  return <div className="page-head"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{text}</p></div>{action}</div>
}

/** Cabeçalho de painel: ícone, título e descrição, com contagem opcional à
 * direita. Mesma dupla ícone + título do MetricCard, para os painéis não
 * ficarem como a única superfície sem iconografia. */
export function PanelTitle({ icon: Icon, title, text, count }: { icon: LucideIcon; title: string; text: string; count?: number }) {
  return <div className="panel-title">
    <div className="panel-title-main">
      <span className="panel-icon" aria-hidden="true"><Icon/></span>
      <div><h2>{title}</h2><p>{text}</p></div>
    </div>
    {count !== undefined && <span>{count}</span>}
  </div>
}

export function Bars({ data, currency = false }: { data: MonthlyPoint[]; currency?: boolean }) {
  const maximum = Math.max(...data.map(point => point.value), 1)
  const compactCurrency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 1 })
  if (!data.length) return <Empty>Ainda não há histórico suficiente.</Empty>
  return <div className="bar-chart" role="list" aria-label={currency ? 'Valores mensais em reais' : 'Quantidade de clientes por mês'}>
    {data.map(point => {
      const exactValue = currency ? brl.format(point.value) : integer.format(point.value)
      return <div className="bar-column" key={point.month} role="listitem" aria-label={`${point.month}: ${exactValue}`}>
        <div className="bar-track" aria-hidden="true"><i className={point.value === 0 ? 'zero' : ''} style={{ height: `${Math.max(point.value / maximum * 140, 0)}px` }} title={exactValue}>
          <span className="bar-value">{currency && point.value >= 1000 ? compactCurrency.format(point.value) : exactValue}</span>
        </i></div>
        <small aria-hidden="true">{monthLabel(point.month)}</small>
      </div>
    })}
  </div>
}

function monthLabel(value: string) {
  const [year, month] = value.split('-').map(Number)
  return new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit' }).format(new Date(year, month - 1, 1)).replace('.', '')
}

export function statusLabel(status: string) {
  return ({ approved: 'Aprovado', pending: 'Pendente', in_process: 'Em processamento', authorized: 'Autorizado', rejected: 'Recusado', cancelled: 'Cancelado', expired: 'Expirado', refunded: 'Estornado', charged_back: 'Chargeback' } as Record<string, string>)[status] || status || 'Não informado'
}

